/**
 * Theme v2 — typography tokens.
 *
 * Inter is the brand font, already loaded via `@fontsource/inter` at the
 * app level. The fallback chain matches the prototype.
 *
 * Size scale uses semantic names (xs..3xl + heading-h1/h2) rather than
 * raw pixel values in components — keeps the design system the source
 * of truth, makes one place to retune the scale.
 */
export const typography = {
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",

  // Per-step font sizes, in pixels. AntD's base maps to `md` (14px) per
  // the prototype's `fontSize: 14`. The `headingH*` slots match the
  // prototype's `fontSizeHeading1` / `fontSizeHeading2`.
  size: {
    xs: 11,
    sm: 12,
    md: 14, // body default
    lg: 16,
    xl: 18,
    '2xl': 22,
    '3xl': 28,
    headingH2: 32,
    headingH1: 42,
  },

  // Weights — restrict to the four we actually use. Inter ships variable
  // so any 100–900 works, but limiting tokens keeps designs disciplined.
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Two line-heights cover 95% of cases. `tight` for headings + labels,
  // `body` (matches prototype's 1.5715) for paragraphs.
  lineHeight: {
    tight: 1.2,
    medium: 1.3,
    body: 1.5715,
  },

  // Letter spacing — used sparingly. `tight` for big display headings
  // (-2% per design); `wide` for uppercase labels in modals.
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.04em',
  },
} as const;

export type Typography = typeof typography;
